import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Heart, ShieldCheck, Search, Users, Store, Copy, ExternalLink,
  MapPin, Sparkles,
} from "lucide-react";
import NonprofitBadge from "@/components/NonprofitBadge";
import DonateDialog from "@/components/DonateDialog";

/**
 * Nonprofits — public directory landing page at /nonprofits.
 *
 * A shareable, on-brand showcase of every verified 501(c)(3) on
 * AddVendTour. Organizers can send donors + sponsors a link to this page
 * and know it always reflects the current roster of verified orgs.
 *
 * Search + role filter live client-side (fast, no round-trips). Deep-link
 * support via `?q=…` and `?role=vendor|organizer` so a shared link can
 * pre-scope to a niche ("all nonprofit vendors in food" style).
 */
export default function Nonprofits() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [donateTarget, setDonateTarget] = useState(null); // {id, orgName}

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    if (search.get("q")) setQ(search.get("q"));
    if (search.get("role")) setRoleFilter(search.get("role"));
    (async () => {
      try {
        const { data } = await api.get("/nonprofits/verified", { params: { limit: 200 } });
        setItems(data.items || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load nonprofits");
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return items.filter((x) => {
      if (roleFilter !== "all" && x.role !== roleFilter) return false;
      if (!qq) return true;
      return (
        (x.nonprofit_org_name || "").toLowerCase().includes(qq) ||
        (x.name || "").toLowerCase().includes(qq) ||
        (x.business_name || "").toLowerCase().includes(qq) ||
        (x.city || "").toLowerCase().includes(qq) ||
        (x.categories || []).some((c) => (c || "").toLowerCase().includes(qq))
      );
    });
  }, [items, q, roleFilter]);

  const copyShareLink = async () => {
    const url = new URL(window.location.href);
    url.search = "";
    if (q) url.searchParams.set("q", q);
    if (roleFilter !== "all") url.searchParams.set("role", roleFilter);
    try {
      await navigator.clipboard.writeText(url.toString());
      toast.success("Share link copied — paste it in your donor / sponsor email.");
    } catch {
      toast.error("Copy failed — please copy the URL from the address bar.");
    }
  };

  return (
    <div data-testid="nonprofits-page">
      {/* Hero */}
      <div className="mx-auto max-w-5xl px-6 lg:px-10 pt-14 pb-8">
        <div
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full"
          style={{ backgroundColor: "rgba(132,204,22,0.14)", color: "#4B7A0A", border: "1.5px solid #84CC16" }}
        >
          <Heart className="h-3 w-3" fill="#84CC16" strokeWidth={0} /> Nonprofit directory
        </div>
        <h1 className="font-serif text-5xl lg:text-6xl mt-3 leading-[1.02]">
          <span style={{ color: "var(--avt-lime)" }}>Support the</span>{" "}
          <span style={{ color: "var(--avt-purple)" }}>501(c)(3)s</span>{" "}
          <span style={{ color: "var(--avt-orange)" }}>doing the work</span>
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed" style={{ color: "var(--ink-soft)" }}>
          Every organization below has been verified as a registered 501(c)(3) via the IRS Publication 78 list.
          Show up to their event, throw a follow at their profile, or forward this page to a donor who&rsquo;s
          been looking for a cause worth backing.
        </p>

        {/* Toolbar */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search
              className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: "var(--ink-soft)" }}
            />
            <Input
              data-testid="nonprofits-search"
              placeholder="Search by name, city, or category…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
          <div
            className="inline-flex rounded-full p-1 border"
            style={{ borderColor: "var(--border-warm)", backgroundColor: "var(--sand)" }}
            data-testid="nonprofits-role-toggle"
          >
            {[
              { k: "all", label: "All", Icon: Sparkles },
              { k: "vendor", label: "Vendors", Icon: Users },
              { k: "organizer", label: "Organizers", Icon: Store },
            ].map(({ k, label, Icon }) => (
              <button
                key={k}
                data-testid={`nonprofits-role-${k}`}
                onClick={() => setRoleFilter(k)}
                className="px-4 py-2 text-xs uppercase tracking-widest rounded-full transition-all inline-flex items-center gap-1.5"
                style={{
                  backgroundColor: roleFilter === k ? "var(--avt-purple)" : "transparent",
                  color: roleFilter === k ? "#fff" : "var(--ink-soft)",
                }}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
          <Button
            onClick={copyShareLink}
            variant="outline"
            data-testid="nonprofits-share-btn"
            className="rounded-full inline-flex items-center gap-1.5"
            size="sm"
          >
            <Copy className="h-3.5 w-3.5" /> Share this page
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div className="mx-auto max-w-5xl px-6 lg:px-10 pb-16">
        {loading ? (
          <div className="py-20 text-center" data-testid="nonprofits-loading">
            <div className="inline-block h-8 w-8 rounded-full border-2 animate-spin"
                 style={{ borderColor: "var(--avt-purple)", borderTopColor: "transparent" }} />
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="nonprofits-empty"
            className="mt-8 card-warm rounded-xl p-10 text-center"
          >
            <Heart className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--avt-pink)" }} />
            <div className="font-serif text-2xl">
              {items.length === 0 ? "No verified nonprofits yet" : "No matches"}
            </div>
            <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
              {items.length === 0
                ? "Run a nonprofit? Get verified in about 5 seconds — we'll list you here."
                : "Try a different search or role filter."}
            </p>
            {items.length === 0 && (
              <Link
                to="/verify-nonprofit"
                data-testid="nonprofits-empty-cta"
                className="btn-primary inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-full"
              >
                <ShieldCheck className="h-4 w-4" /> Get verified
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm" style={{ color: "var(--ink-soft)" }} data-testid="nonprofits-count">
              Showing <strong>{filtered.length}</strong> verified {filtered.length === 1 ? "nonprofit" : "nonprofits"}
              {roleFilter !== "all" && <> · {roleFilter}s only</>}
              {q && <> · matching &ldquo;{q}&rdquo;</>}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((np) => {
                const publicUrl = np.slug ? `/@${np.slug}` : (np.role === "organizer" ? "/events" : null);
                const displayName = np.nonprofit_org_name || np.business_name || np.name;
                return (
                  <div
                    key={np.id}
                    data-testid={`nonprofit-card-${np.id}`}
                    className="card-warm rounded-xl p-5 flex flex-col"
                  >
                    <div className="flex items-start gap-3">
                      {np.avatar_url ? (
                        <img
                          src={np.avatar_url}
                          alt=""
                          className="h-12 w-12 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div
                          className="h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: "rgba(107,47,186,0.10)", color: "var(--avt-purple)" }}
                        >
                          <Heart className="h-5 w-5" fill="var(--avt-purple)" strokeWidth={0} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-serif text-lg leading-tight truncate">{displayName}</div>
                        <div className="mt-1"><NonprofitBadge user={{ nonprofit_status: "verified" }} size="sm" /></div>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1 text-xs" style={{ color: "var(--ink-soft)" }}>
                      <div className="inline-flex items-center gap-1.5">
                        {np.role === "organizer"
                          ? <Store className="h-3 w-3" style={{ color: "var(--avt-orange)" }} />
                          : <Users className="h-3 w-3" style={{ color: "var(--avt-teal)" }} />}
                        <span className="uppercase tracking-widest">{np.role}</span>
                      </div>
                      {np.city && (
                        <div className="inline-flex items-center gap-1.5">
                          <MapPin className="h-3 w-3" /> {np.city}
                        </div>
                      )}
                    </div>
                    {np.bio && (
                      <p className="mt-3 text-sm leading-snug line-clamp-3">{np.bio}</p>
                    )}
                    <div className="mt-4 pt-4 flex-1 flex flex-col gap-3" style={{ borderTop: "1px dashed rgba(0,0,0,0.08)" }}>
                      {np.accepts_donations && (
                        <button
                          data-testid={`nonprofit-donate-${np.id}`}
                          onClick={() => setDonateTarget({ id: np.id, orgName: displayName })}
                          className="btn-primary rounded-full py-2 text-xs uppercase tracking-widest inline-flex items-center justify-center gap-1.5"
                        >
                          <Heart className="h-3.5 w-3.5" fill="#fff" strokeWidth={0} /> Donate
                        </button>
                      )}
                      {publicUrl ? (
                        <Link
                          to={publicUrl}
                          data-testid={`nonprofit-card-${np.id}-link`}
                          className="text-xs inline-flex items-center gap-1 underline"
                          style={{ color: "var(--avt-teal)" }}
                        >
                          <ExternalLink className="h-3 w-3" />
                          {np.role === "organizer" ? "See their events" : "View profile"}
                        </Link>
                      ) : (
                        <span className="text-xs" style={{ color: "var(--ink-soft)" }}>
                          Profile setup in progress
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      <DonateDialog
        open={!!donateTarget}
        onOpenChange={(o) => !o && setDonateTarget(null)}
        nonprofitUserId={donateTarget?.id}
        orgName={donateTarget?.orgName}
      />
    </div>
  );
}
